import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useWeb3Context } from '../../contexts/web3Context'
import TailwindModal from '../ui/TailwindModal'
import GameButton from '../GameButton'
import { getSharedPvPSocket } from '../../socket/pvpSharedSocket'
import {
  ALLIANCE_LEADERSHIP_CHALLENGE_UPDATE_EVENT,
  respondAllianceLeadershipChallenge,
  getAllianceOverview,
  ALLIANCE_LEADERSHIP_CHALLENGE_STAKE_ECS,
} from '../../common/allianceApi'
import { getProfile } from '../../common/api'
import { alert } from '../Alert/Alert'
import { hasEstablishedGameSession } from '../../utils/sessionExpiration'

const norm = (w: string | undefined | null) => (w || '').toLowerCase().trim()

const MODAL_PANEL_BG =
  'linear-gradient(180deg, rgba(118, 92, 68, 0.68) 0%, rgba(102, 80, 58, 0.64) 25%, rgba(88, 68, 50, 0.62) 50%, rgba(74, 58, 44, 0.64) 85%, rgba(62, 48, 38, 0.66) 100%)'
const BROWN_CARD_BORDER = 'rgba(88, 68, 50, 0.55)'
const HEADER_BORDER_COLOR = 'rgb(62, 48, 38)'
const BROWN_LABEL_TEXT = 'rgba(228, 220, 200, 0.8)'
const BROWN_ACCENT_TEXT = '#b8986a'
const MODAL_3D_SHADOW =
  '0 2px 6px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.05) inset, 0 -1px 0 rgba(0,0,0,0.2) inset'
const BTN_PAIR =
  '!w-[12rem] sm:!w-[13rem] !h-[3.25rem] sm:!h-[3.5rem] !min-h-0 !max-h-none box-border inline-flex items-center justify-center shrink-0 text-center !px-2 sm:!px-3 !py-0 leading-tight'

type LeadershipSocketPayload = {
  reason?: string
  leadershipChallenge?: {
    challengeId: string
    status: string
    challengerWallet: string
    leaderWallet: string
    stakeEcs?: number
    pvpRoomId?: string | null
  } | null
  roomId?: string
  leaderWallet?: string
  challengerWallet?: string
}

type LeadershipChallengeRow = NonNullable<NonNullable<LeadershipSocketPayload['leadershipChallenge']>>

/**
 * Alliance leadership duel prompts on any route: leader gets accept/decline modal on challenge;
 * challenger is redirected to character selection + alert when leader accepts.
 */
export const AllianceLeadershipChallengeGlobalHost: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { address } = useWeb3Context()

  const [leaderOpen, setLeaderOpen] = useState(false)
  const [leaderChallengeId, setLeaderChallengeId] = useState<string | null>(null)
  const [leaderStake, setLeaderStake] = useState(ALLIANCE_LEADERSHIP_CHALLENGE_STAKE_ECS)
  const [leaderBusy, setLeaderBusy] = useState(false)

  const lastLeaderPromptChallengeIdRef = useRef<string | null>(null)
  const lastChallengerAcceptedRoomRef = useRef<string | null>(null)

  useEffect(() => {
    if (!address) {
      lastLeaderPromptChallengeIdRef.current = null
      lastChallengerAcceptedRoomRef.current = null
      setLeaderOpen(false)
      setLeaderChallengeId(null)
    }
  }, [address])

  const refreshPlayer = useCallback(() => {
    if (!address) return
    const char =
      typeof global !== 'undefined'
        ? String((global as { currentCharacterName?: string }).currentCharacterName || '')
        : ''
    void getProfile(address, char)
  }, [address])

  const maybeOpenLeaderModal = useCallback(
    (lc: LeadershipChallengeRow) => {
      if (!address) return
      if (lc.status !== 'pending_accept') return
      if (norm(lc.leaderWallet) !== norm(address)) return
      const id = lc.challengeId
      if (!id || lastLeaderPromptChallengeIdRef.current === id) return
      lastLeaderPromptChallengeIdRef.current = id
      setLeaderStake(Number(lc.stakeEcs) || ALLIANCE_LEADERSHIP_CHALLENGE_STAKE_ECS)
      setLeaderChallengeId(id)
      setLeaderOpen(true)
    },
    [address],
  )

  const handleLeaderRespond = async (accept: boolean) => {
    if (!address || !leaderChallengeId) return
    setLeaderBusy(true)
    try {
      const res = await respondAllianceLeadershipChallenge(address, leaderChallengeId, accept)
      if (res?.success === false) {
        alert.open(
          res?.message ||
            t('alliance.leadershipRespondFailed', { defaultValue: 'Action failed.' }),
        )
        return
      }
      setLeaderOpen(false)
      refreshPlayer()
      if (accept && res?.roomId) {
        navigate(`/pvp/alliance-leadership/${encodeURIComponent(String(res.roomId))}`)
        return
      }
      lastLeaderPromptChallengeIdRef.current = null
      if (!accept) {
        alert.open(
          res?.message ||
            t('alliance.leadershipDeclinedOk', { defaultValue: 'Declined. Stake returned to the challenger.' }),
        )
      }
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as Error).message)
          : t('alliance.leadershipRespondFailed', { defaultValue: 'Action failed.' })
      alert.open(msg)
    } finally {
      setLeaderBusy(false)
    }
  }

  useEffect(() => {
    if (!address || !hasEstablishedGameSession()) return
    let cancelled = false
    void (async () => {
      try {
        const overview = await getAllianceOverview(address)
        if (cancelled || overview?.success === false) return
        const lc = overview?.leadershipChallenge
        if (
          lc &&
          lc.status === 'pending_accept' &&
          norm(lc.leaderWallet) === norm(address)
        ) {
          maybeOpenLeaderModal(lc)
        }
      } catch {
        /* ignore */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [address, maybeOpenLeaderModal])

  useEffect(() => {
    if (!address || !hasEstablishedGameSession()) return

    const socket = getSharedPvPSocket(address)
    const onPayload = (payload: LeadershipSocketPayload) => {
      const reason = payload?.reason
      const lc = payload?.leadershipChallenge
      const chW = payload.challengerWallet || lc?.challengerWallet

      if (reason === 'created' && lc != null) {
        maybeOpenLeaderModal(lc)
        return
      }

      if (reason === 'accepted') {
        const roomId = payload.roomId || lc?.pvpRoomId
        if (!roomId || !chW) return
        if (norm(chW) !== norm(address)) return
        if (lastChallengerAcceptedRoomRef.current === roomId) return
        lastChallengerAcceptedRoomRef.current = roomId
        navigate(`/pvp/alliance-leadership/${encodeURIComponent(roomId)}`)
        window.setTimeout(() => {
          alert.open(
            t('alliance.leadershipAcceptedModalBody', {
              defaultValue:
                'The alliance leader accepted the duel. Pick 3 heroes to fight for leadership.',
            }),
          )
        }, 320)
        refreshPlayer()
        return
      }

      if (reason === 'declined' && chW && norm(chW) === norm(address)) {
        lastLeaderPromptChallengeIdRef.current = null
        refreshPlayer()
        alert.open(
          t('alliance.leadershipDeclinedByLeaderModal', {
            defaultValue: 'The alliance leader declined your challenge. Your stake was returned.',
          }),
        )
        return
      }

      if (reason === 'completed') {
        lastLeaderPromptChallengeIdRef.current = null
        lastChallengerAcceptedRoomRef.current = null
        setLeaderOpen(false)
        setLeaderChallengeId(null)
        refreshPlayer()
      }
    }

    socket.on(ALLIANCE_LEADERSHIP_CHALLENGE_UPDATE_EVENT, onPayload)
    return () => {
      socket.off(ALLIANCE_LEADERSHIP_CHALLENGE_UPDATE_EVENT, onPayload)
    }
  }, [address, maybeOpenLeaderModal, navigate, refreshPlayer, t])

  if (!address || !hasEstablishedGameSession()) return null

  return (
    <TailwindModal
      open={leaderOpen}
      onClose={() => {
        if (leaderBusy) return
      }}
      closeOnBackdropClick={false}
      disableEscapeKeyDown={leaderBusy}
      zIndex={21000}
      backdropStyle={{
        backdropFilter: 'blur(4px)',
        backgroundColor: 'rgba(0, 0, 0, 0.82)',
      }}
    >
      <div className="absolute top-1/2 left-1/2 w-[95vw] max-w-[560px] -translate-x-1/2 -translate-y-1/2 outline-none">
        <div
          className="relative w-full overflow-hidden rounded-2xl backdrop-blur-xl"
          style={{
            background: MODAL_PANEL_BG,
            border: `2px solid ${BROWN_CARD_BORDER}`,
            boxShadow: `0 20px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px ${HEADER_BORDER_COLOR}, ${MODAL_3D_SHADOW}`,
          }}
        >
          <div className="relative flex min-h-[220px] flex-col items-center justify-center gap-4 px-4 py-7 text-center sm:px-8">
            <p
              className="font-['Tilt_Warp'] text-xl font-bold tracking-wide sm:text-2xl"
              style={{ color: BROWN_LABEL_TEXT, textShadow: '0 2px 8px rgba(0,0,0,0.95)' }}
            >
              {t('alliance.leadershipGlobalChallengeTitle', {
                defaultValue: 'Leadership duel challenge',
              })}
            </p>
            <div
              className="h-px w-full"
              style={{
                background: `linear-gradient(90deg, transparent, ${BROWN_ACCENT_TEXT}66, ${BROWN_CARD_BORDER}, transparent)`,
              }}
            />
            <p
              className="px-1 text-sm leading-relaxed sm:text-base"
              style={{ color: BROWN_LABEL_TEXT, textShadow: '0 1px 4px rgba(0,0,0,0.85)' }}
            >
              {t('alliance.leadershipGlobalChallengeBody', {
                stake: leaderStake,
                defaultValue:
                  'A member paid {{stake}} Gems to challenge you for alliance leadership. Accept to fight a 3v3 PvP duel, or decline to return their stake.',
              }).replace('{{stake}}', String(leaderStake))}
            </p>
            {leaderBusy ? (
              <div className="flex flex-col items-center gap-2 py-2" aria-live="polite">
                <div
                  className="h-10 w-10 animate-spin rounded-full border-[3px] border-amber-700/40 border-t-amber-300"
                  role="status"
                />
                <p className="text-sm" style={{ color: BROWN_ACCENT_TEXT }}>
                  {t('common.loading', { defaultValue: 'Loading…' })}
                </p>
              </div>
            ) : null}
            <div className="mt-2 flex w-full flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center sm:gap-6">
              <GameButton
                variant="plate"
                size="small"
                disabled={leaderBusy}
                className={BTN_PAIR}
                onClick={() => void handleLeaderRespond(true)}
              >
                {t('alliance.leadershipAcceptDuel', { defaultValue: 'Accept duel (PvP)' })}
              </GameButton>
              <GameButton
                variant="cancel"
                size="small"
                disabled={leaderBusy}
                className={BTN_PAIR}
                onClick={() => void handleLeaderRespond(false)}
              >
                {t('alliance.leadershipDecline', { defaultValue: 'Decline (refund challenger)' })}
              </GameButton>
            </div>
          </div>
        </div>
      </div>
    </TailwindModal>
  )
}
